import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid, Autocomplete, TextField } from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getLocalItem } from "../../util/helper";
import { API_ENDPOINT_URL } from "../../util/api";
import axios from "axios";
import Loading from "../../components/Loading";
import TableApiAcessStats from "../../components/TableApiAcessStats";
import Charts from "../../components/Charts";

const columns = [
  {
    field: "",
    headerName: "",
  },
  {
    field: "access_date",
    headerName: "access date",
  },
  {
    field: "ip_address",
    headerName: "ip address",
  },
  {
    field: "user",
    headerName: "user",
  },
  {
    field: "api_name",
    headerName: "api name",
  },
];

const ApiAccessStats = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [validateDate, setValidateDate] = useState(false);
  const [validateStartDate, setValidateStartDate] = useState(false);
  const [validateEndDate, setValidateEndDate] = useState(false);
  const [validateText, setValidateText] = useState("");
  const [user, setUser] = useState("");
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dataCharts, setDataCharts] = useState([]);
  const [dataChartsV2, setDataChartsV2] = useState([]);

  const token = getLocalItem("key");

  useEffect(() => {
    if (!startDate || !endDate) {
      setValidateDate(false);
      setValidateStartDate(!startDate);
      setValidateEndDate(!endDate);
      setValidateText("");
    } else if (startDate && endDate) {
      const diffDate = startDate.diff(endDate, "days");
      const valid = diffDate <= 0 ? false : true;

      const months = endDate.diff(startDate, "month", true);
      const over3Months = months > 3;
      if (valid) {
        setValidateText("End date must be greater than the Start date.");
      } else if (over3Months) {
        setValidateText("Date range must be within 3 months.");
      } else {
        setValidateText("");
        setValidateDate(false);
        return;
      }

      setValidateStartDate(!startDate);
      setValidateEndDate(!endDate);
      setValidateDate(true);
    } else {
      setValidateDate(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    const queryString = `page=${page}&start_date=${start}&end_date=${end}&user=${user}`;
    const url = `${API_ENDPOINT_URL}/api-access-log/list/?${queryString}`;
    fetchApiAcessLogList(url);
    fetchApiAcessLogUserList();
  }, []);

  const fetchApiAcessLogUserList = async () => {
    const url = `${API_ENDPOINT_URL}/api-access-log/user-list/`;
    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setUserList(response.data.users);
      })
      .catch((error) => {
        console.error("Error fetching user list:", error);
      });
  };

  const handleSearchClick = async () => {
    if (validateDate) {
      return;
    }
    setPage(1);
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    const queryString = `page=1&start_date=${start}&end_date=${end}&user=${user}`;
    const url = `${API_ENDPOINT_URL}/api-access-log/list/?${queryString}`;
    await fetchApiAcessLogList(url);
  };

  const fetchApiAcessLogList = async (url) => {
    setLoading(true);

    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setData(response.data);
        setPage(response.data.page);
        setDataCharts(response.data.summary);
        setDataChartsV2(response.data.chart_data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setLoading(false));
  };

  const nextPage = async () => {
    const nextUrl = data.next;
    await fetchApiAcessLogList(nextUrl);
  };

  const previousPage = async () => {
    const previousUrl = data.previous;
    await fetchApiAcessLogList(previousUrl);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  return (
    <Box sx={{ width: "100%", fontSize: 20 }}>
      <Box
        sx={{
          marginTop: "40px",
          marginBottom: "30px",
          fontSize: 30,
          width: "100%",
        }}
      >
        API Access  Stats
      </Box>
      <Box
        sx={{
          marginLeft: "15px",
          width: "100%",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
          }}
        >
          <Grid container spacing={0}>
            <Grid item lg={6} md={8} sm={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  mt: 1,
                  mr: 1,
                }}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  sx={{ textAlign: "left" }}
                >
                  <Typography
                    mr={1}
                    variant="body1"
                    fontSize={20}
                    color="#707070"
                  >
                    Date:
                  </Typography>
                  <DatePicker
                    value={startDate}
                    onChange={handleStartDateChange}
                    label="Start Date *"
                    format="YYYY/MM/DD"
                    slotProps={{ actionBar: { actions: ["today", "clear"] } }}
                    sx={{
                      ...(validateStartDate && {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          {
                            border: "1px solid red",
                          },
                      }),
                    }}
                  />
                  <Typography
                    variant="body1"
                    m={1}
                    fontSize={20}
                    color="#707070"
                  >
                    ~
                  </Typography>
                  <DatePicker
                    value={endDate}
                    onChange={handleEndDateChange}
                    label="End Date *"
                    format="YYYY/MM/DD"
                    slotProps={{ actionBar: { actions: ["today", "clear"] } }}
                    sx={{
                      ...(validateEndDate && {
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          {
                            border: "1px solid red",
                          },
                      }),
                    }}
                  />
                </LocalizationProvider>
              </Box>
              {validateDate && (
                <Typography sx={{ color: "red", textAlign: "left" }}>
                  {validateText}
                </Typography>
              )}
            </Grid>
            <Grid item lg={3} md={8} sm={12} xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 5,
                  mt: 1,
                }}
              >
                <Typography fontSize={20} color="#707070" mr={1}>
                  User:
                </Typography>

                <Autocomplete
                  options={["", ...userList]}
                  value={user}
                  onChange={(e, newValue) => setUser(newValue)}
                  sx={{ width: "100%" }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField {...params} label="" size="medium" />
                  )}
                  renderOption={(props, option, state) => {
                    const idx = state.index;
                    const total = 1 + userList.length;
                    return (
                      <li
                        {...props}
                        style={{
                          wordBreak: "break-all",
                          minHeight: "40px",
                          borderBottom:
                            idx === total - 1 ? "none" : "1px solid #F5F5F5",
                        }}
                      >
                        {option}
                      </li>
                    );
                  }}
                />
              </Box>
            </Grid>
            <Grid item lg={3} md={4} sm={12} xs={12}>
              <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                <Box
                  padding="11px"
                  sx={{
                    backgroundColor: "#2699fb",
                    color: "white",
                    borderRadius: "4px",
                    "&:hover": {
                      cursor: "pointer",
                    },
                    width: "200px",
                  }}
                  onClick={handleSearchClick}
                >
                  <Typography m={0} cursor="pointer" alignSelf="center">
                    Search
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box item sx={{ width: "100%", mt: 4, marginLeft: "15px" }}>
        <Charts
          title="Access Graph"
          data={dataChartsV2.data}
          maxValue={dataChartsV2.maxValue}
          pxPerPoint={50}
        />
      </Box>

      <Typography
        variant="body1"
        gutterBottom
        textAlign="left"
        marginLeft="10px"
        my={3}
        cursor="pointer"
        fontSize={26}
      >
        Access Log
      </Typography>

      <TableApiAcessStats
        columns={columns}
        rows={data.results}
        page={page}
        pageSize={data.page_size}
        lastPageNumber={data.last_page_number}
        nextPage={nextPage}
        previousPage={previousPage}
        sx={{
          marginLeft: "10px",
          "& .MuiTableCell-root": { fontSize: 18 },
        }}
      />

      <Loading open={loading} message="Loading ..." />
    </Box>
  );
};

export default ApiAccessStats;
